import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, ArrowLeft, Loader2, Zap, ExternalLink, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import logoMark from "@/assets/logo-mark.png";

const PAYMENT_LINK = "https://buy.stripe.com/6oU00k4M719UcQd3Uz1Fe01";
const PORTAL_LINK  = "https://billing.stripe.com/p/login/cNibJ20vR3i2dUh1Mr1Fe00";

export default function PlanosPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [polling, setPolling]     = useState(false);
  const [user, setUser]           = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const isPaid = searchParams.get("paid") === "1";

  const fetchSubscription = useCallback(async (userId) => {
    const { data } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    return data;
  }, []);

  const hasAccess = (sub) => {
    if (!sub) return false;
    if (sub.status === "active") return true;
    if (sub.status === "trial") {
      const end = sub.trial_ends_at ? new Date(sub.trial_ends_at) : null;
      return end && new Date() < end;
    }
    return false;
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) { navigate("/"); return; }
      setUser(session.user);
      const sub = await fetchSubscription(session.user.id);
      setSubscription(sub);
      setLoading(false);

      // Se veio do Stripe (?paid=1), fica fazendo polling até assinatura aparecer
      if (isPaid && !hasAccess(sub)) {
        setPolling(true);
      }
    });
  }, [navigate, fetchSubscription, isPaid]);

  // Polling após pagamento — verifica a cada 3 segundos por até 60 segundos
  useEffect(() => {
    if (!polling || !user) return;

    const interval = setInterval(async () => {
      const sub = await fetchSubscription(user.id);
      setSubscription(sub);
      setPollCount(c => c + 1);

      if (hasAccess(sub)) {
        setPolling(false);
        clearInterval(interval);
        setTimeout(() => navigate("/dashboard", { replace: true }), 1000);
      }
    }, 3000);

    const timeout = setTimeout(() => {
      setPolling(false);
      clearInterval(interval);
    }, 60000);

    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [polling, user, navigate, fetchSubscription]);

  const handleAssinar = () => {
    const email = user?.email || "";
    const url = email
      ? `${PAYMENT_LINK}?prefilled_email=${encodeURIComponent(email)}&client_reference_id=${user?.id}`
      : PAYMENT_LINK;
    // Redireciona para Stripe — após pagamento, Stripe redireciona para /planos?paid=1
    window.location.href = url;
  };

  const isActive  = subscription?.status === "active";
  const isTrial   = subscription?.status === "trial";
  const canManage = hasAccess(subscription);

  const trialEnd  = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
  const daysLeft  = trialEnd ? Math.max(0, Math.ceil((trialEnd - Date.now()) / (1000*60*60*24))) : 0;

  const proFeatures = [
    "Fluxo de caixa automático",
    "Contas recorrentes com vencimento",
    "Meta de investimento mensal",
    "Categorias personalizadas",
    "Dashboard anual completo",
    "Projeções financeiras",
    "Funciona no celular e PC",
    "Dados na nuvem",
  ];

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#00E548] animate-spin"/>
    </div>
  );

  // Estado de aguardando pagamento
  if (isPaid && polling) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4" style={{ fontFamily:"Sora, sans-serif" }}>
        <div className="text-center max-w-sm">
          <Loader2 className="w-12 h-12 text-[#00E548] animate-spin mx-auto mb-6"/>
          <h2 className="text-xl font-bold text-white mb-2">Confirmando seu pagamento...</h2>
          <p className="text-[#6B6B7B] text-sm">Aguarde alguns segundos. Você será redirecionado automaticamente.</p>
          <div className="mt-4 flex justify-center gap-1">
            {Array.from({length:3}).map((_,i) => (
              <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#00E548] animate-bounce" style={{animationDelay:`${i*150}ms`}}/>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Estado de pagamento confirmado mas aguardando
  if (isPaid && !polling && !canManage && pollCount > 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4" style={{ fontFamily:"Sora, sans-serif" }}>
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">⏳</div>
          <h2 className="text-xl font-bold text-white mb-2">Processando seu pagamento</h2>
          <p className="text-[#6B6B7B] text-sm mb-6">Pode levar alguns segundos. Clique em verificar para tentar novamente.</p>
          <button onClick={() => setPolling(true)} className="btn-primary flex items-center gap-2 mx-auto">
            <RefreshCw size={16}/> Verificar acesso
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily:"Sora, sans-serif" }}>
      <header className="border-b border-[#1A1A24] bg-black/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-3xl mx-auto flex h-14 items-center justify-between px-4">
          {canManage ? (
            <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#6B6B7B] hover:text-white transition-colors">
              <ArrowLeft size={16}/><span className="text-sm">Voltar ao app</span>
            </button>
          ) : (
            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#6B6B7B] hover:text-white transition-colors">
              <ArrowLeft size={16}/><span className="text-sm">Página inicial</span>
            </button>
          )}
          <div className="flex items-center gap-2">
            <img src={logoMark} alt="logo" width={24} height={24}/>
            <span className="text-sm font-bold">Conta em Dia</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">

        {/* Saudação personalizada */}
        {user && (
          <p className="text-sm text-[#6B6B7B] mb-6">
            Olá, <span className="text-white font-medium">{user.user_metadata?.full_name || user.email}</span>
          </p>
        )}

        {/* Status banner */}
        {isActive ? (
          <div className="bg-[#00E548]/10 border border-[#00E548]/30 rounded-2xl p-5 mb-8 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#00E548]/20 flex items-center justify-center">
              <Check size={20} className="text-[#00E548]"/>
            </div>
            <div className="flex-1">
              <p className="font-bold text-white">Plano Pro ativo ✅</p>
              <p className="text-sm text-[#6B6B7B]">Sua assinatura está em dia.</p>
            </div>
          </div>
        ) : isTrial && daysLeft > 0 ? (
          <div className="bg-[#FFD93D]/10 border border-[#FFD93D]/30 rounded-2xl p-5 mb-8 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#FFD93D]/20 flex items-center justify-center">
              <Zap size={20} className="text-[#FFD93D]"/>
            </div>
            <div className="flex-1">
              <p className="font-bold text-white">Trial ativo — {daysLeft} dia{daysLeft !== 1 ? "s" : ""} restante{daysLeft !== 1 ? "s" : ""}</p>
              <p className="text-sm text-[#6B6B7B]">Assine antes de expirar para não perder acesso.</p>
            </div>
          </div>
        ) : (
          <div className="bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 rounded-2xl p-5 mb-8">
            <p className="font-bold text-white mb-1">Assine para ter acesso 🚀</p>
            <p className="text-sm text-[#6B6B7B]">Você está logado com <strong className="text-white">{user?.email}</strong>. Assine abaixo para liberar o acesso completo ao Conta em Dia.</p>
          </div>
        )}

        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold mb-2">Plano Pro</h1>
          <p className="text-[#6B6B7B]">Tudo que você precisa para ter previsibilidade financeira real.</p>
        </div>

        {/* Card do plano */}
        <div className="rounded-2xl border-2 border-[#00E548]/40 bg-[#0F0F14] p-8 mb-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#00E548]/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"/>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-5xl font-extrabold text-white">R$ 19,90</span>
            <span className="text-[#6B6B7B] mb-1">/mês</span>
          </div>
          <p className="text-sm text-[#6B6B7B] mb-6">Sem fidelidade anual forçada. Cancele quando quiser.</p>
          <ul className="space-y-3 mb-8">
            {proFeatures.map(f => (
              <li key={f} className="flex items-center gap-3">
                <Check size={16} className="text-[#00E548] flex-shrink-0" strokeWidth={3}/>
                <span className="text-sm text-white">{f}</span>
              </li>
            ))}
          </ul>

          {isActive ? (
            <div className="w-full py-3 rounded-xl bg-[#00E548]/10 border border-[#00E548]/30 text-[#00E548] text-center font-semibold">
              ✅ Você já é assinante Pro
            </div>
          ) : (
            <button onClick={handleAssinar}
              className="w-full py-4 rounded-xl font-bold text-black text-base transition-all hover:-translate-y-0.5"
              style={{ background:"#00E548", boxShadow:"0 8px 30px -8px rgba(0,229,72,0.6)" }}>
              Assinar agora — R$ 19,90/mês
            </button>
          )}
        </div>

        {/* Botão gerenciar */}
        {canManage && (
          <button onClick={() => window.open(PORTAL_LINK, "_blank")}
            className="w-full py-3 rounded-xl border border-[#1A1A24] bg-[#0F0F14] text-sm text-[#8B8B9E] hover:text-white hover:border-[#2A2A35] transition-colors flex items-center justify-center gap-2 mb-4">
            <ExternalLink size={14}/> Gerenciar assinatura no Stripe
          </button>
        )}

        <p className="text-center text-xs text-[#3A3A48]">Pagamento seguro via Stripe.</p>

        {/* ULTRA em breve */}
        <div className="rounded-2xl border border-[#1A1A24] bg-[#0F0F14] p-6 mt-4 opacity-60">
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-white">ULTRA — R$ 29,90/mês</span>
            <span className="text-xs border border-[#1A1A24] rounded-full px-2 py-0.5 text-[#6B6B7B]">Em breve</span>
          </div>
          <p className="text-sm text-[#6B6B7B]">Tudo do Pro + Chat com IA para análises financeiras personalizadas.</p>
        </div>
      </main>
    </div>
  );
}
